<template>
  <div>
    <div
      v-if="currentStepIndex === 0"
      class="division-info-container"
    >
      <div
        class="division-info"
        v-html="division['discount_html']">
      </div>

      <div class="location-bar" v-if="soilItems.length">
        <span>{{ division['name'] }}</span>

        <a class="tel" :href="`tel:${ partner['phone_number'] }`">
          {{partner['phone_number']}}
        </a>
      </div>
    </div>

    <div v-if="customErrors.length > 0" class="info-message error-message">
      <ul>
        <li v-for="errorMessage in customErrors" :key="errorMessage.id">{{ errorMessage }}</li>
      </ul>
    </div>
    <div v-if="customMessages.length > 0" class="info-message">
      <ul>
        <li v-for="message in customMessages" :key="message.id">{{ message }}</li>
      </ul>
    </div>
    <section v-if="loadingScreen">
      <div
        class="loading-screen"
      >
        <span class="helper-text">Loading&hellip;</span>
      </div>
    </section>

    <section v-else>
      <div v-if="soilItems.length">
        <component ref="currentStepComponent" :is="currentStep" />

        <order-summary
          :editable="currentStepIndex < 4"
          :soilEditable="soilEditable"
          :class="{ 'narrow-summary' : currentStepIndex === 5 }"
        />

        <order-summary-flyout
          ref="orderSummaryFlyout"
          :editable="currentStepIndex < 4"
          :soilEditable="soilEditable"
        >
          <div class="order-footer-buttons">
            <button
              @click="$refs.orderSummaryFlyout.hideFlyout()"
              class="flat-green-btn continue-shopping-btn"
              :disabled="pricingAvailabilityChanged"
            >
              Continue Shopping
            </button>

            <button
              @click="next"
              class="flat-green-btn"
              :disabled="pricingAvailabilityChanged"
            >
              Continue Checkout
            </button>
            <a
              @click="reset"
              class="restart-button"
              href="#"
            >
              Restart
            </a>
          </div>
        </order-summary-flyout>

        <div class="order-footer" v-if="currentStepIndex < 5">
          <div style="flex: 1">
            <button
              v-if="currentStepIndex != 0"
              @click="back"
              class="flat-green-btn"
            >
              Back
            </button>
          </div>
          <div class="order-footer-buttons" v-if="currentStepIndex != 4">
            <button
              @click="next"
              class="flat-green-btn"
              :disabled="pricingAvailabilityChanged"
            >
              Continue Checkout
            </button>
            <a
              @click="reset"
              class="restart-button"
              href="#"
            >
              Restart
            </a>
          </div>
        </div>
      </div>
      <div v-if="noPostalCode">
        <postal-input></postal-input>
      </div>
    </section>
    <postal-popup></postal-popup>
    <ad-division-mismatch></ad-division-mismatch>
  </div>
</template>

<script>
import CustomerInfo from './steps/customerInfo.vue';
import DeliveryDate from './steps/deliveryDate.vue';
import Fertilizer from './steps/fertilizer.vue';
import OrderSummary from './orderSummary.vue';
import OrderSummaryFlyout from './orderSummaryFlyout.vue';
import OrderReceipt from './steps/orderReceipt.vue';
import PaymentInfo from './steps/paymentInfo.vue';
import Soil from './steps/soil.vue';
import PostalPopup from '../../containers/orders/postalPopup.vue';
import PostalInput from '../../containers/orders/postalInput.vue';
import AdDivisionMismatch from '../../containers/orders/adDivisionMismatch.vue';

export default {
  components: {
    CustomerInfo,
    DeliveryDate,
    Fertilizer,
    OrderSummary,
    OrderSummaryFlyout,
    OrderReceipt,
    PaymentInfo,
    Soil,
    PostalPopup,
    PostalInput,
    AdDivisionMismatch,
  },

  created() {
    if (this.$store.state.customer.address.postal === '') {
      this.$store.commit('setProperties', { loading: false });
    } else {
      this.$store.dispatch('fetchPricing');
      this.$store.dispatch('loadLocalCart');
    }

    // General created hooks
    this.$store.dispatch('trackSpotifyPageView');
    this.$store.dispatch('trackRedditPageView');
  },

  computed: {

    pricingAvailabilityChanged() {
      return this.$store.getters.pricingAvailabilityChanged;
    },

    noPostalCode() {
      return this.postalCode.length < 1;
    },

    postalCode() {
      return this.$store.state.customer.address.postal;
    },

    soilEditable() {
      if ((this.currentStepIndex !== 0) && (this.bulkItemArray.length <= 1)) {
        return false;
      }
      return true;
    },

    loadingScreen() {
      return this.$store.state.loading;
    },

    currentStepIndex() {
      return this.$store.state.orderStepIndex;
    },
    /**
     * Current Order step
     */
    currentStep() {
      return this.steps()[this.currentStepIndex];
    },

    /**
     * List of soil products
     */
    soilItems() {
      return this.$store.state.soilItems;
    },

    otherItems() {
      return this.$store.state.otherItems;
    },

    bulkItemArray() {
      return this.$store.getters.bulkItemArray;
    },

    /**
     * Division header info
     */
    division() {
      return this.$store.state.division;
    },

    partner() {
      return this.$store.state.partner;
    },

    customErrors() {
      return this.$store.state.customErrors;
    },

    customMessages() {
      return this.$store.state.customMessages;
    },
  },

  methods: {
    /**
     * Order steps
     */
    steps() {
      return [
        Soil,
        Fertilizer,
        DeliveryDate,
        CustomerInfo,
        PaymentInfo,
        OrderReceipt,
      ];
    },

    /**
     * Moves to next Order step
     */
    next() {
      // This is where I can probably validate whether a partner has products, and skip step if necessary
      // Clear errors & messages on submit button to ready for new ones.
      this.$store.commit('setProperties', { customErrors: [], customMessages: [] });

      const [canGoToNextStep, errorMessage] = this.validateNextStep();
      const nextStep = this.getNextStepIndex();

      if (canGoToNextStep) {
        // If child component has validators, run them
        if (this.$refs.currentStepComponent.validate) {
          let stepIsValid = false;

          this.$refs.currentStepComponent.validate().then((isValid) => {
            stepIsValid = isValid;

            if (!stepIsValid) {
              // Scroll to top of page
              window.scrollTo(0, 0);
              return;
            }
            this.$store.commit('setProperties', { orderStepIndex: nextStep });
          });
        } else {
          this.$store.commit('setProperties', { orderStepIndex: nextStep });
        }
      } else {
        this.addError(errorMessage);
      }

      // Scroll to top of page
      window.scrollTo(0, 0);
    },

    validateNextStep() {
      const nextStep = this.steps()[this.currentStepIndex + 1];

      if (nextStep.name === 'Fertilizer') {
        // Check length of bulk Items in store to ensure they have selected products
        const hasBulkItems = Object.keys(this.$store.state.bulkProductLineItems).length > 0;
        const response = hasBulkItems ? [true, null] : [false, 'Please select at least one item'];
        return response;
      }
      if (nextStep.name === 'CustomerInfo') {
        const hasDeliveryDate = Object.keys(this.$store.state.deliveryDate).length > 0;
        const response = hasDeliveryDate ? [true, null] : [false, 'Please select a delivery date'];
        return response;
      }
      return [true, null];
    },

    getNextStepIndex() {
      const nextStep = this.steps()[this.currentStepIndex + 1];
      let nextStepIndex = this.currentStepIndex + 1;
      if (nextStep.name === 'Fertilizer') {
        const hasOtherItems = this.otherItems.length > 0;
        nextStepIndex = hasOtherItems ? nextStepIndex : (nextStepIndex + 1);
      }
      return nextStepIndex;
    },

    /*
     * Returns to the previous step
     */
    back() {
      // Reset error messages
      this.$store.commit('setProperties', {
        customErrors: [],
        customMessages: [],
      });

      const prevStep = this.getPrevStepIndex();
      this.$store.commit('setProperties', { orderStepIndex: prevStep });
      // Scroll to top of page
      window.scrollTo(0, 0);
    },

    getPrevStepIndex() {
      const prevStep = this.steps()[this.currentStepIndex - 1];
      let prevStepIndex = this.currentStepIndex - 1;
      if (prevStep.name === 'Fertilizer') {
        const hasOtherItems = this.otherItems.length > 0;
        prevStepIndex = hasOtherItems ? prevStepIndex : (prevStepIndex - 1);
      }
      return prevStepIndex;
    },

    /**
     * Returns to first Order step
     */
    reset() {
      this.$store.commit('setProperties', { postalModal: { showModal: true, postalError: '' } });
      // window.scrollTo(0, 0);
    },

    addError(errorMessage) {
      this.$store.commit('addToPropertyArrays', { customErrors: [errorMessage] });
    },
  },
};
</script>

<style>

*:focus {
  outline: none;
}

::-webkit-input-placeholder { text-transform: uppercase; }
::-moz-placeholder { text-transform: uppercase; }
:-ms-input-placeholder { text-transform: uppercase; }

.content {
  max-width: 1024px;
  min-height: 62vh;
  padding: 0 1em;
  padding-top: 1em;
}

  .location-bar {
    background-color: black;
    color: white;
    text-transform: uppercase;
    display: flex;
    padding: 0.62em;
    font-size: 18px;
    font-weight: bold;
  }

  .location-bar .tel {
    flex: 1;
    text-align: right;
    color: inherit;
  }

  .products {
    font-size: 13px;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;
    list-style-type: none;
  }

  .products li {
    max-width: calc(33.33% - 5.24em);
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 1.62em 2.62em;
    text-align: center;
  }

  .centered-text {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }

  .small-button {
    font-family: inherit;
    font-size: 12px;
    color: black;
    background-color: #FFE52C;
    border-color: #009245;
    box-shadow: none;
    border-radius: 2.62em;
    font-weight: bold;
    flex: 1;
    margin: 0 .38em;
    text-shadow: none;
    text-transform: uppercase;
  }

  .small-button:hover {
    color: white;
  }

  hr {
    border: none;
    border-bottom: 1px solid #CCCCCC;
    margin: 1.62em 0;
  }

  .order-summary {
    background-color: #F4F4F4;
    color: black;
    padding: 1.62em 2.62em;
  }

  .narrow-summary {
    max-width: 768px;
    margin: 0 auto;
    margin-bottom: 2.62em;
  }

  .order-summary h2 {
    color: #009245;
    text-transform: uppercase;
    font-size: 1.38em;
  }

  .order-summary hr {
    border-color: #009245;
  }

  .order-summary ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  .order-summary ul li {
    display: flex;
    align-items: center;
  }

  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .products h1 {
    font-weight: bold;
    font-size: 22px;
    color: #009144;
  }

  .products h2 {
    font-weight: bold;
    font-size: 22px;
    color: #009144;
    margin: 0.62rem 0;
  }

  .products h3 {
    font-size: 14px;
    color: black;
    font-style: italic;
    margin: 0.62rem 0;
  }

  .products li .price {
    color: #009144;
    font-weight: bold;
    font-size: 18px;
    margin: 0.62rem 0;
    text-align: center;
  }

  .order-summary .price {
    text-align: right;
  }

  .product-info {
    display: flex;
    width: 100%;
    margin-top: 1em;
  }

  .product-info .small-button:first-of-type {
    margin-left: 0;
  }

  .product-info .small-button:last-of-type {
    margin-right: 0;
  }

  .products li .flat-green-btn {
    width: 100%;
    margin-top: 1rem;
  }

  .flat-red-btn {
    width: 100%;
    margin-top: 1rem;
    background-color: hsl(0,62%,50%);
    color: white;
  }

  .total-section {
    display: flex;
    padding: 1em 1.62em 0 1.62em;
  }

  .total {
    font-weight: bold;
    flex: 1;
    text-align: right;
  }

  .total-text {
    text-transform: uppercase;
    font-weight: bold;
  }

  .subtotal-section {
    text-align: right;
  }

  .order-footer {
    border-top: 1px solid #CCCCCC;
    margin-top: 2.62em;
    padding: 2.62em;
    display: flex;
    justify-content: flex-end;
  }

  .order-footer-buttons {
    align-self: flex-end;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .restart-button {
    margin-top: 1em;
  }

  button:disabled {
    filter: saturate(0%);
    cursor: not-allowed;
  }

  .loading-screen {
    min-height: 62vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .info-message {
    background-color: #FFF9C4;
    border-left: 3px solid #ffe500;
    margin: 1em;
    padding: 1em;
    display: flex;
  }

  .info-message ul {
    margin: 0px;
  }

  .info-message > *:first-child {
    flex: 1;
  }

  .info-message button {
    flex: 0;
  }

  .error-message {
    background-color: #FFE6E8;
    border-left: none;
  }

  .error-message li {
    color: #cc0015;
  }

  .division-info-container {
    margin-top: -1em;
  }

  .division-info table {
    line-height: 0;
  }

  .division-info img {
    width: 100%;
    display: block;
    max-width: none !important;
  }

  @media (max-width: 640px) {
    .division-info {
      margin: 0 -1em;
    }

    .location-bar {
      margin: 0 -1em;
      padding: 0.62em 1em;
      flex-direction: column;
      align-items: flex-start;
    }

    .location-bar .tel {
      margin-top: .38em;
    }

    .order-summary {
      margin: 0 -1em;
    }

    .order-footer {
      margin: 2.62em 0;
      padding: 1.62em 0;
    }
  }

  .continue-shopping-btn {
    margin-bottom: 0.5em;
    background: #359f3e;
  }
</style>
